import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import updatePrincipalService from '../../services/principals/updatePrincipalService';
import principalService from '../../services/principals/principalService';
import PrincipalForm from './PrincipalForm';
import { TABS, ASSIGNED_ROLES_TYPES } from '../../constants';
import { mapPrincipalCommunityApps } from '../../helpers/principals/mapPrincipalCommunityApps';

const EditPrincipal = ({
  open,
  dispatchUpdatePrincipalPut,
  updatePrincipalFetch,
  dispatchPrincipalGet,
  principalFetch,
  organisationId,
  urlRoot,
  isSystemOrg,
  forbidManageBecauseSystemOrg,
  id,
  returnRoute,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleSubmit = useCallback(
    body => {
      dispatchUpdatePrincipalPut(
        id,
        {
          ...body,
          organisation: { id: organisationId },
          communityApps: mapPrincipalCommunityApps(body.communityApps),
        },
        () => navigate(returnRoute || `${urlRoot}/${TABS.PRINCIPALS}`),
        t
      );
    },
    [urlRoot, returnRoute, id, organisationId, dispatchUpdatePrincipalPut, navigate, t]
  );

  useEffect(() => {
    if (open && id) {
      dispatchPrincipalGet(id, t);
    }
  }, [id, open, t, dispatchPrincipalGet]);

  const defaultData = useMemo(() => {
    if (id !== principalFetch.value?.id || principalFetch.pending) {
      return false;
    }

    // Extract user group IDs from assignedRoles
    const userGroupIds = new Set();
    (principalFetch.value?.assignedRoles || []).forEach(role => {
      const userGroupSources = role.sources?.filter(
        s => s.type === ASSIGNED_ROLES_TYPES.USER_GROUP
      );
      userGroupSources?.forEach(source => {
        if (source.id) userGroupIds.add(source.id);
      });
    });

    return {
      ...(principalFetch.value || {}),
      primaryPhoneNumber: principalFetch.value?.primaryPhoneNumber || '',
      userGroups: Array.from(userGroupIds),
      communityApps: (principalFetch.value?.communityApps || []).map(ca => ({
        app: `${ca.appInstallation?.tag}_${ca.community?.tag}`,
        features: (ca.communityAppFeatures || []).map(caf => ({
          feature: caf.appInstallationFeature?.tag,
          roles: (caf.communityAppFeatureRoles || []).map(r => r.appInstallationFeatureRole?.tag),
        })),
      })),
      inviteRequest: {
        invitationType: 1, // temporary fixed (1 email), will later come from api
      },
    };
  }, [principalFetch, id]);

  if (!open) return null;

  return (
    <PrincipalForm
      header={
        <span>
          {t('editUser')}&nbsp;-&nbsp;{principalFetch.value?.fullName}
        </span>
      }
      onSubmit={handleSubmit}
      submitProcessing={updatePrincipalFetch.pending}
      returnRoute={returnRoute || `${urlRoot}/${TABS.PRINCIPALS}`}
      cancelRoute={returnRoute || `${urlRoot}/${TABS.PRINCIPALS}`}
      dataLoading={principalFetch.pending}
      data={!principalFetch.pending && defaultData}
      forbidManageBecauseSystemOrg={forbidManageBecauseSystemOrg}
      organisationId={organisationId}
      isSystemOrg={isSystemOrg}
      principal={principalFetch.value}
      showRemoveButton
      editMode
    />
  );
};

EditPrincipal.propTypes = {
  organisationId: PropTypes.string.isRequired,
  urlRoot: PropTypes.string.isRequired,
  dispatchUpdatePrincipalPut: PropTypes.func.isRequired,
  updatePrincipalFetch: PropTypes.object.isRequired,
  dispatchPrincipalGet: PropTypes.func.isRequired,
  principalFetch: PropTypes.object.isRequired,
  forbidManageBecauseSystemOrg: PropTypes.bool.isRequired,
  isSystemOrg: PropTypes.bool.isRequired,
  id: PropTypes.string.isRequired,
  returnRoute: PropTypes.string,
  open: PropTypes.bool,
};

EditPrincipal.defaultProps = { open: false, returnRoute: undefined };

export default compose(principalService, updatePrincipalService)(EditPrincipal);
